"use client";

import { LocationAutocomplete } from "@/components/ui/create-event/LocationAutocomplete";
import { FormattedLocation } from "@/hooks/useLocationSearch";
import { useCallback, useId, useMemo } from "react";
import { selectedFromFormatted } from "../../_logic/location-utils";
import { StepProps } from "../../_types/wizard-types";

/**
 * Optional "stay near" place on the location screen: a venue, office or landmark the group wants
 * its hotel close to. The event's hotel search opens anchored on it, framed on its surroundings,
 * with the place pinned. Uses the same Places search as the location field above it.
 *
 * @param props.formData - Wizard form data; reads `nearPlace`.
 * @param props.dispatch - Wizard dispatch; writes `SET_NEAR_PLACE`.
 * @returns The labeled single-place picker.
 * @throws Never.
 */
export const NearPlaceField = ({ formData, dispatch }: Pick<StepProps, "formData" | "dispatch">) => {
  // Hooks
  const headingId = useId();

  // Derived values
  // Older saved wizard state predates the field, so it can be missing rather than null.
  const nearPlace = formData.nearPlace ?? null;
  const nearPlaces = useMemo(() => (nearPlace ? [nearPlace] : []), [nearPlace]);
  const selected = useMemo(() => selectedFromFormatted(nearPlaces), [nearPlaces]);

  // Handlers
  const handleChange = useCallback(
    (_selected: string[], formatted: FormattedLocation[]) => {
      dispatch({ type: "SET_NEAR_PLACE", payload: formatted[0] ?? null });
    },
    [dispatch]
  );

  // Render
  return (
    <div role="group" aria-labelledby={headingId} className="flex flex-col gap-2 pt-6">
      <p id={headingId} className="text-sm font-medium text-gray-900">
        Somewhere specific to stay near? <span className="text-gray-400">(optional)</span>
      </p>
      <LocationAutocomplete
        searchType="address"
        selectedLocations={selected}
        formattedLocations={nearPlaces}
        onLocationChange={handleChange}
        limit={1}
        placeholder="A venue, office, or landmark"
      />
      <p className="text-xs font-medium text-gray-500">Your hotel search will start here, nearest hotels first.</p>
    </div>
  );
};
